// @ts-strict-ignore
import {
	ChangeDetectorRef,
	Directive,
	DoCheck,
	EventEmitter,
	Injector,
	Input,
	IterableDiffer,
	IterableDiffers,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {NotificationService, SimRecorderService, SimRecordingsService, SimUtilityService} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {remove} from "lodash-es";
import Enumerable from "linq";
import {ClientType, DurationMarker, Marker, MarkerType, Scenario, ScenarioRecording} from "@models";
import {DetachableChangeDetection} from "../../panel-components/detachable-cd/detachable-change-detection";
import {Translations} from "@translations";

@Directive()
export abstract class BaseBookmarkPane implements OnChanges, DoCheck, DetachableChangeDetection {
	T = Translations;
	ClientType = ClientType;

	@Input() ascending: boolean;
	@Input({required: true}) scenarioRecording: ScenarioRecording;
	@Input() scenario: Scenario;
	@Input() showChecklistItems: boolean;
	@Output() jumpToMarker: EventEmitter<Marker> = new EventEmitter();

	protected _simRecorderService: SimRecorderService;
	protected _simNotificationService: NotificationService;
	private _simRecordingService: SimRecordingsService;
	public translate: TranslateService;
	private iterableDiffer: IterableDiffer<Marker>;
	private attachedToChangeDetection: boolean;
	protected lastClickedMarkerId: string;

	protected constructor(
		protected _injector: Injector,
		protected differs: IterableDiffers,
		protected changeDetector: ChangeDetectorRef,
		protected emitterService: EventEmitterService,
	) {
		this.iterableDiffer = differs.find(<Marker[]>[]).create<Marker>(null);
		this.initializeServices();

		emitterService.onScenarioRecordingChanged.subscribe(async (args) => {
			if (this.scenarioRecording && this.scenarioRecording.id === args.scenarioRecordingId) {
				this.scenarioRecordingChanged();
			}
		});
	}

	private initializeServices() {
		this._simRecorderService = this._injector.get(SimRecorderService);
		this._simNotificationService = this._injector.get(NotificationService);
		this._simRecordingService = this._injector.get(SimRecordingsService);
		this.translate = this._injector.get(TranslateService);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.scenarioRecording) {
			this.scenarioRecordingChanged();
		}
	}

	public ngDoCheck() {
		if (this.attachedToChangeDetection) {
			const change = this.iterableDiffer.diff(this.scenarioRecording?.markers);

			let addedCount = 0;

			if (change) {
				change.forEachAddedItem((marker) => {
					addedCount++;
				});

				if (addedCount > 0) {
					this.prepareMarkersForUi();
					this.initializeDurationLines();
				}
			}
		}
	}

	private scenarioRecordingChanged() {
		this.prepareMarkersForUi();
		this.initializeDurationLines();
		this.changeDetector.markForCheck();
	}

	public toggleFavorite(marker: Marker) {
		this._simRecordingService
			.setFavoriteMarker({
				markerId: marker.markerId,
				scenarioRecordingId: this.scenarioRecording.id,
				setFavoriteValue: !marker.isFavorited,
			})
			.then(() => {
				marker.isFavorited = !marker.isFavorited;
			});
	}

	public markerClicked(clickedMarker: Marker) {
		if (this.jumpToMarker?.observers?.length >= 1) {
			setTimeout(() => {
				this.lastClickedMarkerId = clickedMarker.markerId;
			}, 100);

			for (const marker of this.scenarioRecording.markers) {
				marker["active"] = false;
			}

			clickedMarker["active"] = true;
			this.jumpToMarker.next(clickedMarker);
		}
	}

	protected initializeDurationLines() {
		if (this.scenarioRecording?.markers && this.scenarioRecording.markers.length > 0) {
			// Explanation:
			// if "start" is true we show a start node.
			// if "end" is true we show a end node
			// we can have 5 lines therefore the array has 3 elements.

			const durationMarkerLines = [
				{
					visible: false,
					start: false,
					end: false,
					openDurationIcons: new Array<string>(),
				},
				{
					visible: false,
					start: false,
					end: false,
					openDurationIcons: new Array<string>(),
				},
				{
					visible: false,
					start: false,
					end: false,
					openDurationIcons: new Array<string>(),
				},
				{
					visible: false,
					start: false,
					end: false,
					openDurationIcons: new Array<string>(),
				},
				{
					visible: false,
					start: false,
					end: false,
					openDurationIcons: new Array<string>(),
				},
			];

			let openDurationMarkers = 0;

			const availableColors = durationMarkerLines.length;

			let recordingMarkers: Marker[] = null;

			if (this.ascending) {
				recordingMarkers = Enumerable.from(this.scenarioRecording.markers).toArray();
			} else {
				recordingMarkers = Enumerable.from(this.scenarioRecording.markers).reverse().toArray();
			}

			// durationClass: "endDuration"
			// durationClass: "middleDuration"
			// durationClass: "beginDuration"

			for (const marker of recordingMarkers) {
				// Check if duration marker
				if (marker.markerType === MarkerType.DurationMarker) {
					const durationMarker = marker as DurationMarker;

					// Check if start marker or not
					if (
						durationMarker.startPosition === durationMarker.position &&
						durationMarker.endPosition !== durationMarker.position
					) {
						// Start marker
						durationMarkerLines[openDurationMarkers % availableColors].visible = true;
						durationMarkerLines[openDurationMarkers % availableColors].openDurationIcons.push(marker.icon?.id);

						durationMarkerLines[openDurationMarkers % availableColors].start = true;

						durationMarker["durationMarkerLines"] = SimUtilityService.copy(durationMarkerLines);

						durationMarkerLines[openDurationMarkers % availableColors].start = false;

						openDurationMarkers++;
					} else {
						// End marker

						// Getting line of end duration marker
						const durationMarkerLineOfOpenDurationMarker: {
							visible: boolean;
							start: boolean;
							end: boolean;
							openDurationIcons: string[];
						} = Enumerable.from(durationMarkerLines).firstOrDefault((durationMarkerLine) => {
							return durationMarkerLine.openDurationIcons.indexOf(marker.icon?.id) >= 0;
						});

						if (durationMarkerLineOfOpenDurationMarker) {
							durationMarkerLineOfOpenDurationMarker.end = true;

							durationMarker["durationMarkerLines"] = SimUtilityService.copy(durationMarkerLines);

							durationMarkerLineOfOpenDurationMarker.end = false;

							if (durationMarkerLineOfOpenDurationMarker) {
								// Remove icon from open icon list
								durationMarkerLineOfOpenDurationMarker.openDurationIcons = Enumerable.from(
									durationMarkerLineOfOpenDurationMarker.openDurationIcons,
								)
									.where((openDurationIcon) => {
										return openDurationIcon !== durationMarker.icon?.id;
									})
									.toArray();

								if (durationMarkerLineOfOpenDurationMarker.openDurationIcons.length === 0) {
									durationMarkerLineOfOpenDurationMarker.visible = false;
								}
							}
						}
					}
				} else {
					marker["durationMarkerLines"] = SimUtilityService.copy(durationMarkerLines);
				}
			}
		}
	}

	public deleteMarker(markerId: string) {
		const deleteMarkerRequest = {
			markerId: markerId,
			recordingId: this.scenarioRecording.id,
		};

		this._simRecordingService.deleteMarker(deleteMarkerRequest).then(
			(deleteMarkerResult) => {
				remove(this.scenarioRecording.markers, {markerId: markerId});
			},
			() => {
				this._simNotificationService.notifyError(this.translate.instant(this.T.BOOKMARK_PANE_MARKER_DELETE_ERROR));
			},
		);
	}

	private prepareMarkersForUi() {
		if (this.scenarioRecording?.markers && this.scenarioRecording.markers.length > 0) {
			// ======================================================
			// Remove checklist items is necessary
			// ======================================================
			if (!this.showChecklistItems) {
				for (const marker of this.scenarioRecording.markers) {
					if (marker.markerType === MarkerType.ChecklistMarker) {
						marker["show"] = false;
					}
				}
			}

			// ======================================================
			// Sort markers
			// ======================================================
			if (this.ascending) {
				this.scenarioRecording.markers.sort((markerA, markerB) => {
					return markerA.position - markerB.position;
				});
			} else {
				this.scenarioRecording.markers.sort((markerA, markerB) => {
					return markerB.position - markerA.position;
				});
			}

			// TODO: This navigates to the marker position
			// setTimeout(()=>{
			// 	let markerEntries = document.getElementsByClassName("holder");
			// 	let markerEntryLast = markerEntries[0];
			// 	markerEntryLast.getElementsByTagName("textarea")[0]?.click();
			// }, 500);
		}
	}

	detachFromCD() {
		this.attachedToChangeDetection = false;
		this.changeDetector.detach();
	}

	attachToCD() {
		this.attachedToChangeDetection = true;
		this.changeDetector.reattach();
	}
}
